import { useCallback, useEffect, useRef, useState } from 'react';
import {
  computeViewportFrame, isEditableElement, isEditableFocusedWithin,
  resolveDampedFrame, OFFSET_TOP_SETTLE_MS,
} from './viewportFrame';
import { kbLog } from '../../utils/ui/keyboardReframeDebug';
import { recordKeyboardHeight } from '../../utils/ui/inlineKeyboardGuard';
import { keyboardKindFor } from '../../utils/ui/inlineRevealPlan';
import { noteKeyboardObserved } from '../../utils/ui/keyboardPresence';

/**
 * Tracks the visible viewport frame (visualViewport) for keyboard-aware overlays.
 * On iOS Safari, `position: fixed` modals use the layout viewport — when the
 * keyboard opens, the bottom of a centered modal sits behind it unless the
 * scrim is reframed (see `computeKeyboardOverlayStyles`).
 *
 * Detection is gated on a focused editable element (see `computeViewportFrame`)
 * so a stale `visualViewport.height` after keyboard dismissal — the iOS
 * standalone-PWA quirk — can't strand the modal in its keyboard layout. To make
 * that gate fire on dismissal we MUST also listen to `focusout`/`focusin` (and
 * `window.resize`): in standalone iOS the `visualViewport` `resize` event that
 * would otherwise reset us frequently doesn't fire on keyboard close.
 *
 * `containerEl` scopes the focus gate to a modal's OWN subtree:
 *   - omitted (undefined) → GLOBAL "any editable focused" signal (BottomTabBar).
 *   - a node or null      → SCOPED; the keyboard counts as open for this overlay
 *     only when the focused editable is inside `containerEl`. This stops a
 *     background field that merely kept focus (the invoices search box) from
 *     reframing an unrelated, input-less dialog (the "mark as paid" confirm that
 *     pinned itself under the status bar). null = node not mounted yet → closed.
 */
export function useVisualViewportFrame(enabled, containerEl) {
  const scoped = arguments.length >= 2;
  const read = useCallback(() => {
    if (typeof window === 'undefined') {
      return { keyboardOpen: false, offsetTop: 0, height: 0, obscured: 0 };
    }
    const vv = window.visualViewport;
    const active = typeof document !== 'undefined' ? document.activeElement : null;
    const editableFocused = scoped
      ? isEditableFocusedWithin(active, containerEl)
      : isEditableElement(active);
    return computeViewportFrame({
      innerHeight: window.innerHeight,
      vvHeight: vv ? vv.height : null,
      vvOffsetTop: vv ? vv.offsetTop : 0,
      editableFocused,
    });
  }, [scoped, containerEl]);

  const [frame, setFrame] = useState(read);
  // Damping state: the last COMMITTED frame + the offsetTop value on probation.
  // iOS transiently pans the visual viewport on focus (offsetTop bounces >0 and
  // back); mirroring every intermediate value into the animated reframe is the
  // "modal shifts down then returns" bounce. resolveDampedFrame holds a changed
  // offsetTop until it survives OFFSET_TOP_SETTLE_MS.
  const committedRef = useRef(null);
  const pendingRef = useRef(null);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    const vv = window.visualViewport;
    let rafId = 0;
    let settleTimer = 0;
    const commit = () => {
      const raw = read();
      const { frame: next, pending } = resolveDampedFrame({
        prev: committedRef.current,
        raw,
        pending: pendingRef.current,
        now: Date.now(),
      });
      kbLog('commit', {
        rawOt: raw.offsetTop, rawH: raw.height, rawOpen: raw.keyboardOpen ? 1 : 0,
        ot: next.offsetTop, h: next.height, obs: next.obscured,
        open: next.keyboardOpen ? 1 : 0, pend: pending ? pending.offsetTop : '-',
      });
      pendingRef.current = pending;
      committedRef.current = next;
      // Feed the inline-keyboard-guard's height predictor (dedupes internally).
      // A measured keyboard is also positive proof this device HAS one, which
      // clears any hardware-keyboard suppression learned earlier — so a tablet
      // recovers on the first real keyboard rather than waiting for the
      // guard's own probe. Both writes dedupe internally.
      if (next.keyboardOpen && next.obscured > 0) {
        const activeEl = typeof document !== 'undefined' ? document.activeElement : null;
        if (isEditableElement(activeEl)) recordKeyboardHeight(next.obscured, keyboardKindFor(activeEl));
        noteKeyboardObserved();
      }
      setFrame((cur) => (
        cur.keyboardOpen === next.keyboardOpen
        && cur.offsetTop === next.offsetTop
        && cur.height === next.height
        && cur.obscured === next.obscured
          ? cur : next
      ));
      // A held offsetTop needs a clock, not just events: re-evaluate once the
      // settle window elapses so a sustained pan commits even in silence.
      clearTimeout(settleTimer);
      if (pending) settleTimer = setTimeout(commit, OFFSET_TOP_SETTLE_MS + 20);
    };
    // Coalesce bursty resize/scroll/focus events to one read per frame.
    const update = () => {
      if (typeof requestAnimationFrame !== 'function') { commit(); return; }
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(commit);
    };
    commit(); // sync read on enable so the reframe applies promptly
    window.addEventListener('orientationchange', update);
    window.addEventListener('resize', update);
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);
    if (vv) {
      vv.addEventListener('resize', update);
      vv.addEventListener('scroll', update);
    }
    return () => {
      if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(rafId);
      // The settle timer must die with the effect: left alive, a pending pan's
      // deferred commit would fire after close/re-enable and write through the
      // OLD closure's read/refs. Refs reset too so the next session starts
      // fresh instead of damping against the previous modal's committed frame.
      clearTimeout(settleTimer);
      committedRef.current = null;
      pendingRef.current = null;
      window.removeEventListener('orientationchange', update);
      window.removeEventListener('resize', update);
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', update);
      if (vv) {
        vv.removeEventListener('resize', update);
        vv.removeEventListener('scroll', update);
      }
    };
  }, [enabled, read]);

  return frame;
}
